import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  ScrollView,
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  Pressable,
  Platform,
  RefreshControl,
} from 'react-native';
import { GTFonts, useTheme } from '../../theme';
import {
  capitalizeFirstLetter,
  formatWithCommas,
  s,
  fs,
  v,
  iconSize,
} from '../../utils/libs';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  setSummaryLoading,
  setSummaryError,
  setSummaryData,
  setAnnualLoading,
  setAnnualError,
  setAnnualPoints,
} from '../../redux/backoffice/expensesSlice';
import {
  getMonthlyExpenses,
  getAnnualExpenses,
} from '../../api/backofficeExpenses';
import type { ExpenseSummary } from '../../api/backofficeExpenses';
import DateRangeDropdown from '../../components/Reusables/headers/DateRangeDropdown';
import ExpenseHeroCard from '../../components/backoffice/expenses/ExpenseHeroCard';
import ExpenseInsightCard from '../../components/backoffice/expenses/ExpenseInsightCard';
import CategoryDonutChart from '../../components/charts/CategoryDonutChart';
import AnnualExpensesBarChart from '../../components/charts/AnnualExpensesBarChart';
import moment from 'moment';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/AppStack';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import BackOfficeHeaderLocationPicker from '../../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';

// ─── Helpers ─────────────────────────────────────────────────────────────

type DateRange = {
  start: string;
  end: string;
};

const getDateRangeForSelection = (selection: string): DateRange => {
  switch (selection) {
    case 'Today':
      return {
        start: moment().startOf('day').toISOString(),
        end: moment().endOf('day').toISOString(),
      };
    case 'Yesterday':
      return {
        start: moment().subtract(1, 'day').startOf('day').toISOString(),
        end: moment().subtract(1, 'day').endOf('day').toISOString(),
      };
    case 'Weekly':
      return {
        start: moment().startOf('week').toISOString(),
        end: moment().endOf('week').toISOString(),
      };
    case 'Monthly':
      return {
        start: moment().startOf('month').toISOString(),
        end: moment().endOf('month').toISOString(),
      };
    case 'Yearly':
      return {
        start: moment().startOf('year').toISOString(),
        end: moment().endOf('year').toISOString(),
      };
    default:
      return {
        start: moment().startOf('day').toISOString(),
        end: moment().endOf('day').toISOString(),
      };
  }
};

const getDateRangeLabel = (selection: string): string => {
  switch (selection) {
    case 'Today':
      return moment().format('MMMM D, YYYY');
    case 'Yesterday':
      return moment().subtract(1, 'day').format('MMMM D, YYYY');
    case 'Weekly':
      return `${moment().startOf('week').format('MMM D')} – ${moment().endOf('week').format('MMM D, YYYY')}`;
    case 'Monthly':
      return moment().format('MMMM YYYY');
    case 'Yearly':
      return String(moment().year());
    default:
      return moment().format('MMMM D, YYYY');
  }
};

// ─── Screen ──────────────────────────────────────────────────────────────

const BackOfficeExpensesScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const employee = useAppSelector(
    st => st.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const locationVersion = useAppSelector(st => st.location.locationVersion);
  const year = new Date().getFullYear();

  const summary = useAppSelector(
    st => st.backofficeExpenses.summary ?? { data: null, loading: false, error: null },
  );
  const annual = useAppSelector(
    st => st.backofficeExpenses.annual ?? { points: [], loading: false, error: null },
  );

  const [selectedRange, setSelectedRange] = useState<
    'Today' | 'Yesterday' | 'Weekly' | 'Monthly' | 'Yearly'
  >('Monthly');
  const [selectedDonutIndex, setSelectedDonutIndex] = useState<number | null>(
    null,
  );
  const [refreshing, setRefreshing] = useState(false);

  const donutData = useMemo(() => {
    if (!summary.data?.totalsByType) return [];
    return Object.entries(summary.data.totalsByType)
      .filter(([, amount]) => amount > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([type, amount]) => ({
        label: capitalizeFirstLetter(type),
        value: amount,
      }));
  }, [summary.data?.totalsByType]);

  const fetchSummary = useCallback(async () => {
    if (!owner?.accessToken || !ownerId) return;
    try {
      dispatch(setSummaryLoading(true));
      dispatch(setSummaryError(null));
      const { start, end } = getDateRangeForSelection(selectedRange);
      const res = await getMonthlyExpenses(
        axiosBackoffice as any,
        ownerId,
        { accessToken: owner?.accessToken },
        { startISO: start, endISO: end },
      );
      if (res?.status === 200) {
        const data = res?.data?.data as ExpenseSummary;
        dispatch(setSummaryData(data ?? null));
      } else {
        dispatch(
          setSummaryError(
            String(res?.data?.message || 'Failed to load expenses summary'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(
        setSummaryError(e?.message || 'Error fetching expenses summary'),
      );
    } finally {
      dispatch(setSummaryLoading(false));
    }
  }, [owner?.accessToken, ownerId, dispatch, axiosBackoffice, selectedRange]);

  const fetchAnnual = useCallback(async () => {
    if (!owner?.accessToken || !ownerId) return;
    try {
      dispatch(setAnnualLoading(true));
      dispatch(setAnnualError(null));
      const res = await getAnnualExpenses(
        axiosBackoffice as any,
        ownerId,
        year,
        {
          accessToken: owner?.accessToken,
        },
      );
      if (res?.status === 200) {
        const payload = (res?.data?.data || res?.data || []) as any[];
        const MONTHS = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];

        const byMonth: Array<{
          month: string;
          totalsByType: Record<string, number>;
          total: number;
        }> = MONTHS.map(m => ({ month: m, totalsByType: {}, total: 0 }));

        (payload || []).forEach((entry: any, index: number) => {
          let monthIndex = index;
          const maybeMonth = Number(entry?.month);
          if (
            !Number.isNaN(maybeMonth) &&
            maybeMonth >= 1 &&
            maybeMonth <= 12
          ) {
            monthIndex = maybeMonth - 1;
          }

          const expenses = (entry?.expenses as any) || entry || {};
          const totalsByType =
            (expenses?.totalsByType as Record<string, number>) || {};

          const fallbackMap: Record<string, number> = {};
          if (Object.keys(totalsByType || {}).length === 0) {
            if (typeof expenses?.payments !== 'undefined') {
              fallbackMap.payments = Number(expenses?.payments || 0);
            }
            if (typeof expenses?.stocks !== 'undefined') {
              fallbackMap.stocks = Number(expenses?.stocks || 0);
            }
          }

          const map: Record<string, number> =
            Object.keys(totalsByType || {}).length > 0
              ? totalsByType
              : fallbackMap;

          const totalFromMap = Object.values(map || {}).reduce(
            (acc, v) => acc + Number(v || 0),
            0,
          );
          const total = Number(expenses?.totalExpenses || totalFromMap);

          if (monthIndex >= 0 && monthIndex < 12) {
            byMonth[monthIndex] = {
              month: MONTHS[monthIndex],
              totalsByType: map,
              total,
            };
          }
        });

        dispatch(setAnnualPoints(byMonth));
      } else {
        dispatch(
          setAnnualError(
            String(res?.data?.message || 'Failed to load annual expenses'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setAnnualError(e?.message || 'Error fetching annual expenses'));
    } finally {
      dispatch(setAnnualLoading(false));
    }
  }, [owner?.accessToken, ownerId, dispatch, axiosBackoffice, year]);

  useFocusEffect(
    useCallback(() => {
      fetchSummary();
      fetchAnnual();
    }, [fetchSummary, fetchAnnual, locationVersion]),
  );

  const onRefresh = useCallback(async () => {
    if (refreshing || summary.loading) return;
    setRefreshing(true);
    try {
      await Promise.all([fetchSummary(), fetchAnnual()]);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, summary.loading, fetchSummary, fetchAnnual]);

  const headerRightNode = useMemo(
    () => (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginRight: s(8),
        }}>
        <View style={{ marginLeft: Platform.OS === 'web' ? s(8) : 0 }}>
          <DateRangeDropdown
            variant="compact"
            chipBgColor={'rgba(255,255,255,0.15)'}
            chipTextColor={theme.colors.textInverse}
            chipBorderColor={'rgba(255,255,255,0.35)'}
            selectedOverride={selectedRange}
            onChange={val => setSelectedRange(val)}
          />
        </View>
      </View>
    ),
    [selectedRange, theme.colors.textInverse],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => headerRightNode,
    });
  }, [navigation, headerRightNode]);

  const webHeaderNode = useMemo(
    () => (
      <WebHeaderBar
        title={<BackOfficeHeaderLocationPicker />}
        titleAlign="center"
        rightSlot={headerRightNode}
        onBack={() => {
          try {
            (navigation as any).navigate('Dashboard');
          } catch {
            if (navigation.canGoBack()) navigation.goBack();
          }
        }}
      />
    ),
    [headerRightNode, navigation],
  );
  useWebHeader(webHeaderNode, true);

  const biggestCostInsight = useMemo(() => {
    if (!summary.data?.totalsByType || summary.data.totalExpenses <= 0)
      return null;
    const sorted = Object.entries(summary.data.totalsByType)
      .filter(([, amt]) => amt > 0)
      .sort(([, a], [, b]) => b - a);
    if (sorted.length === 0) return null;
    const [topType, topAmt] = sorted[0];
    const topPct = Math.round((topAmt / summary.data.totalExpenses) * 100);
    const second = sorted.length > 1 ? sorted[1] : null;
    const secondPart = second
      ? `. ${capitalizeFirstLetter(second[0])} follows at ${Math.round((second[1] / summary.data.totalExpenses) * 100)}%.`
      : '.';
    return `${capitalizeFirstLetter(topType)} accounts for ${topPct}% of your spending (KSh ${formatWithCommas(topAmt, 0)})${secondPart}`;
  }, [summary.data]);

  const spendingPaceInsight = useMemo(() => {
    if (!summary.data || summary.data.totalExpenses <= 0) return null;
    const { start } = getDateRangeForSelection(selectedRange);
    const rangeStart = moment(start);
    const now = moment();
    const daysElapsed = Math.max(now.diff(rangeStart, 'days'), 1);
    const dailyRate = Math.round(summary.data.totalExpenses / daysElapsed);

    if (selectedRange !== 'Monthly') return null;
    const daysInMonth = now.daysInMonth();
    const projected = dailyRate * daysInMonth;
    const prevTotal = summary.data.previousPeriod?.totalExpenses || 0;
    const vsPrev =
      prevTotal > 0
        ? Math.round(((projected - prevTotal) / prevTotal) * 100)
        : 0;
    const vsLabel =
      prevTotal > 0
        ? ` — ${Math.abs(vsPrev)}% ${vsPrev >= 0 ? 'more' : 'less'} than last month.`
        : '.';
    return `At KSh ${formatWithCommas(dailyRate, 0)} per day, you're projected to spend KSh ${formatWithCommas(projected, 0)} this month${vsLabel}`;
  }, [summary.data, selectedRange]);

  return (
    <View style={styles.root}>
      <View style={styles.listCard}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }>
          <ExpenseHeroCard
            summary={summary.data}
            loading={summary.loading && !summary.data}
            dateRangeLabel={getDateRangeLabel(selectedRange)}
            onViewExpenses={() =>
              navigation.navigate('BackOfficeLatestExpenses')
            }
          />

          <ExpenseInsightCard
            title="Biggest cost center"
            body={
              biggestCostInsight ||
              'No expenses recorded for this period yet.'
            }
            tone="info"
          />

          <View style={styles.donutCard}>
            <Text style={styles.donutCardTitle}>Where your money goes</Text>
            <CategoryDonutChart
              data={donutData}
              selectedCategoryIndex={selectedDonutIndex}
              onCategoryPress={index =>
                setSelectedDonutIndex(prev =>
                  prev === index ? null : index,
                )
              }
            />
          </View>

          {spendingPaceInsight && (
            <ExpenseInsightCard
              title="Spending pace"
              body={spendingPaceInsight}
              tone="warning"
            />
          )}

          {/* Yearly Expenses — replaces latest expenses list */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Yearly Expenses</Text>
            <AnnualExpensesBarChart data={annual.points} />
          </View>
        </ScrollView>
      </View>

      {Platform.OS === 'web' ? (
        <Pressable
          style={({ pressed }) => [
            styles.floatingButton,
            pressed && styles.floatingButtonPressed,
          ]}
          accessibilityLabel="Add expense"
          onPress={() => navigation.navigate('BackOfficeExpenseCategories')}>
          <MaterialCommunityIcons
            name="cash-plus"
            size={iconSize(26)}
            color={theme.colors.textInverse}
          />
        </Pressable>
      ) : (
        <TouchableOpacity
          style={styles.floatingButton}
          accessibilityLabel="Add expense"
          onPress={() => navigation.navigate('BackOfficeExpenseCategories')}>
          <MaterialCommunityIcons
            name="cash-plus"
            size={iconSize(24)}
            color={theme.colors.textInverse}
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

export default BackOfficeExpensesScreen;

const createStyles = (theme: any) =>
  StyleSheet.create({
    root: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    listCard: {
      flex: 1,
      margin: s(5),
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      ...theme.shadows.sm,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center' as const,
        width: '100%',
        margin: 0,
        marginVertical: s(14),
      }),
    },
    container: {
      flex: 1,
    },
    content: {
      paddingBottom: v(90),
    },
    donutCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: s(14),
      marginHorizontal: s(14),
      // marginTop: v(4),
      padding: s(14),
    },
    donutCardTitle: {
      fontSize: fs(18),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
      marginBottom: v(12),
      textAlign: 'center',
    },
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      marginHorizontal: s(10),
      marginTop: v(24),
      paddingBottom: v(8),
      ...theme.shadows.sm,
    },
    cardTitle: {
      fontFamily: GTFonts.bold,
      textAlign: 'center',
      color: theme.colors.text,
      fontSize: fs(18),
      lineHeight: fs(20),
      marginVertical: v(10),
    },
    floatingButton: {
      position: 'absolute',
      bottom: Platform.OS === 'web' ? v(24) : v(12),
      right: Platform.OS === 'web' ? s(24) : s(16),
      width: Platform.OS === 'web' ? s(60) : s(52),
      height: Platform.OS === 'web' ? s(60) : s(52),
      borderRadius: Platform.OS === 'web' ? theme.borderRadius.md : s(26),
      backgroundColor: theme.colors.successDark,
      alignItems: 'center',
      justifyContent: 'center',
      ...theme.shadows.md,
    } as any,
    floatingButtonPressed: {
      transform: [{ scale: 0.95 }],
    },
  });
